import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Header from '../../components/Header';
import { useCallback, useEffect, useState } from 'react';
import { tokens } from '../../theme';

const SendNotification = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [notificationMode, setNotificationMode] = useState('trip_wise');
  const [tripType, setTripType] = useState('completed');
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [notificationType, setNotificationType] = useState('general');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLimit] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [viewRow, setViewRow] = useState(null);

  const selectedTripObj = trips.find((t) => t.id === selectedTrip) || null;

  useEffect(() => {
    const fetchTrips = async () => {
      setTripsLoading(true);
      setSelectedTrip('');
      setTrips([]);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_APILINK}/api/admin/notifications/completed-trips?type=${tripType}`,
          { headers: { token } }
        );
        const data = await res.json();
        if (data.success) setTrips(data.data || []);
      } catch (e) {
        console.error('Failed to load trips', e);
      } finally {
        setTripsLoading(false);
      }
    };
    fetchTrips();
  }, [token, tripType]);

  const fetchHistory = useCallback(async (page) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/notifications/history?page=${page + 1}&limit=${historyLimit}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setHistory(data.data || []);
        setHistoryTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to load notification history', e);
    } finally {
      setHistoryLoading(false);
    }
  }, [token, historyLimit]);

  useEffect(() => {
    fetchHistory(historyPage);
  }, [historyPage, fetchHistory]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      setToastMessage('Please fill in both Title and Description.');
      setOpenToast(true);
      return;
    }
    if (notificationMode === 'trip_wise' && !selectedTripObj) {
      setToastMessage('Please select a trip.');
      setOpenToast(true);
      return;
    }

    const isBroadcast = notificationMode !== 'trip_wise';
    const url = isBroadcast
      ? `${process.env.REACT_APP_APILINK}/api/admin/notifications/send-all-passengers`
      : `${process.env.REACT_APP_APILINK}/api/admin/notifications/send-feedback`;

    const body = isBroadcast
      ? { title: title.trim(), message: message.trim(), notificationType, targetGroup: notificationMode }
      : {
          countryId: selectedTripObj.countryId,
          departureDate: selectedTripObj.departureDate,
          destination: selectedTripObj.destination,
          title: title.trim(),
          message: message.trim(),
          notificationType,
        };

    setSending(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setToastMessage(data.message || (data.success ? 'Notifications sent!' : 'Failed to send.'));
      setOpenToast(true);
      if (data.success) {
        setSelectedTrip('');
        setTitle('');
        setMessage('');
        setHistoryPage(0);
        fetchHistory(0);
      }
    } catch (e) {
      console.error('Send notification error', e);
      setToastMessage('Something went wrong. Please try again.');
      setOpenToast(true);
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      field: 'sentAt',
      headerName: 'Date',
      width: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'destination',
      headerName: 'Trip',
      flex: 1,
      minWidth: 180,
      cellClassName: 'name-column--cell',
      renderCell: ({ row }) => {
        if (!row.departureDate || row.departureDate === '0000-00-00' || row.countryId === 0) {
          return row.destination;
        }
        return `${row.destination} (${row.departureDate})`;
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1.5,
      minWidth: 200,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'total_passengers',
      headerName: 'Passengers',
      width: 110,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'view',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => setViewRow(row)}
          sx={{
            backgroundColor: colors.blueAccent[500],
            color: colors.grey[100],
            minWidth: 'unset',
            px: 1.5,
            ':hover': { backgroundColor: colors.blueAccent[600] },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb="4px">
        <Header title="SEND NOTIFICATION" subtitle="Send a push notification to passengers of a completed trip" />
      </Box>

      {/* ── Send Form ── */}
      <Box
        p="24px"
        mb="32px"
        borderRadius="8px"
        sx={{ backgroundColor: colors.primary[400] }}
        maxWidth="620px"
      >
        <form onSubmit={handleSend}>
          <Box display="grid" gap="20px">

            {/* ── Mode selector ── */}
            <FormControl variant="filled" fullWidth>
              <InputLabel id="mode-label">Notification Mode</InputLabel>
              <Select
                labelId="mode-label"
                value={notificationMode}
                onChange={(e) => {
                  setNotificationMode(e.target.value);
                  setSelectedTrip('');
                  setTitle('');
                  setMessage('');
                }}
              >
                <MenuItem value="all_passengers_guides">All Passengers &amp; Guides</MenuItem>
                <MenuItem value="all_passengers">All Passengers</MenuItem>
                <MenuItem value="all_guides">All Guides</MenuItem>
                <MenuItem value="trip_wise">Trip-wise Notification</MenuItem>
              </Select>
            </FormControl>

            {/* ── Trip-wise: toggle + trip selector ── */}
            {notificationMode === 'trip_wise' && (
              <>
                <Box>
                  <Typography variant="body2" sx={{ color: colors.grey[300], mb: 1 }}>Trip type</Typography>
                  <ToggleButtonGroup
                    value={tripType}
                    exclusive
                    onChange={(_, val) => { if (val) setTripType(val); }}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        color: colors.grey[300],
                        borderColor: colors.grey[600],
                        px: 3,
                        '&.Mui-selected': {
                          backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100],
                          '&:hover': { backgroundColor: colors.blueAccent[600] },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="completed">Completed Trips</ToggleButton>
                    <ToggleButton value="active">Active Trips</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {tripsLoading ? (
                  <Box display="flex" alignItems="center" gap="8px" py="12px">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color={colors.grey[300]}>Loading trips…</Typography>
                  </Box>
                ) : (
                  <FormControl variant="filled" fullWidth>
                    <InputLabel id="trip-select-label">{tripType === 'active' ? 'Select Active Trip' : 'Select Completed Trip'}</InputLabel>
                    <Select
                      labelId="trip-select-label"
                      value={selectedTrip}
                      onChange={(e) => setSelectedTrip(e.target.value)}
                    >
                      {trips.length === 0 && (
                        <MenuItem value="" disabled>No trips found</MenuItem>
                      )}
                      {trips.map((trip) => (
                        <MenuItem key={trip.id} value={trip.id}>
                          {trip.label}
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.grey[400] }}>
                            ({trip.passengerCount} passenger{trip.passengerCount !== 1 ? 's' : ''})
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedTripObj && (
                  <Typography variant="body2" color={colors.greenAccent[400]}>
                    {selectedTripObj.passengerCount} passenger{selectedTripObj.passengerCount !== 1 ? 's' : ''} will receive this notification.
                  </Typography>
                )}
              </>
            )}

            {/* ── Broadcast info ── */}
            {notificationMode === 'all_passengers_guides' && (
              <Typography variant="body2" color={colors.greenAccent[400]}>
                This notification will be sent to all passengers and guides in the system.
              </Typography>
            )}
            {notificationMode === 'all_passengers' && (
              <Typography variant="body2" color={colors.greenAccent[400]}>
                This notification will be sent to all passengers in the system.
              </Typography>
            )}
            {notificationMode === 'all_guides' && (
              <Typography variant="body2" color={colors.greenAccent[400]}>
                This notification will be sent to all guides in the system.
              </Typography>
            )}

            {/* ── Common fields (shown when broadcast OR trip selected) ── */}
            {(notificationMode !== 'trip_wise' || selectedTrip) && (
              <>
                {notificationMode === 'trip_wise' && (
                  <FormControl>
                    <FormLabel sx={{ color: colors.grey[300], mb: 0.5, '&.Mui-focused': { color: colors.greenAccent[400] } }}>
                      Notification Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={notificationType}
                      onChange={(e) => setNotificationType(e.target.value)}
                    >
                      <FormControlLabel
                        value="general"
                        control={<Radio color="success" size="small" />}
                        label={<Typography variant="body2" color={colors.grey[200]}>General</Typography>}
                      />
                      <FormControlLabel
                        value="feedback_request"
                        control={<Radio color="success" size="small" />}
                        label={<Typography variant="body2" color={colors.grey[200]}>Feedback Request</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  variant="filled"
                  label="Notification Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Description"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  multiline
                  rows={4}
                  required
                />

                <Button
                  type="submit"
                  disabled={sending}
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    width: 'fit-content',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '10px 30px',
                    '&:hover': { backgroundColor: colors.blueAccent[800] },
                    '&.Mui-disabled': { opacity: 0.6 },
                  }}
                >
                  {sending ? <CircularProgress size={20} color="inherit" /> : 'Send Notification'}
                </Button>
              </>
            )}

          </Box>
        </form>
      </Box>

      {/* ── Notification History ── */}
      <Header title="NOTIFICATION HISTORY" subtitle="" />

      <Box
        m="8px 0 0 0"
        height="500px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.grey[100] },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={history}
          columns={columns}
          rowCount={historyTotal}
          loading={historyLoading}
          paginationMode="server"
          paginationModel={{ page: historyPage, pageSize: historyLimit }}
          onPaginationModelChange={({ page }) => setHistoryPage(page)}
          pageSizeOptions={[historyLimit]}
          disableRowSelectionOnClick
        />
      </Box>

      {/* ── View Dialog ── */}
      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: 3,
          }}
        >
          <Typography variant="h5" fontWeight="600">Notification Details</Typography>
          <IconButton size="small" onClick={() => setViewRow(null)} sx={{ color: colors.grey[100] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: colors.primary[400], p: '0 !important' }}>
          {viewRow && (
            <Box>
              {[
                { label: 'Date', value: viewRow.sentAt },
                { label: 'Trip', value: `${viewRow.destination} (${viewRow.departureDate})` },
                { label: 'Total Passengers', value: viewRow.total_passengers },
                { label: 'Title', value: viewRow.title },
                { label: 'Message', value: viewRow.message },
              ].map(({ label, value }, i) => (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    px: 3,
                    py: 2,
                    backgroundColor: i % 2 === 0 ? colors.primary[400] : colors.primary[500],
                  }}
                >
                  <Typography
                    sx={{
                      width: '140px',
                      flexShrink: 0,
                      fontWeight: 600,
                      color: colors.grey[300],
                      fontSize: '13px',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography sx={{ color: colors.grey[100], fontSize: '13px', wordBreak: 'break-word' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <IconButton size="small" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default SendNotification;
